import { el, clear, toast } from '../dom';
import { shopApi, fieldApi, buffsApi, companionsApi, arenaBrApi, foragerApi, accountApi, type Package, type PaymentMethod, type Wallet, type BuffStatus, type CompanionStatus, type EnergyOffer, type HealOffer, type ShroomShelf } from '../game-api';
import { ApiError } from '../../core/network/api';
import { startDeposit, readReturnedOrderId, clearPendingOrder, pollDepositStatus } from '../../core/payments/deposit-flow';
import { getPaymentMethods } from '../../core/payments/payments-api';
import type { DepositMethodId, DepositMethod } from '../../core/payments/types';
// The catalogue rows are idempotency-gated server-side, so their purchase has to
// carry an actionId; this is the one place that mints one per gesture.
import { dispatchAction } from '../action/dispatch';
import type { GameCtx, ViewTeardown } from '../types';
import { richText, gicon, type IconName } from '../ui/icons';
import { itemIconSrc, armourIconSrc } from '../ui/item-icons';
import { tierTint, armourTintTier } from '../map/ores';
import { playSfx } from '../audio/audio';
import { renderCashout, cashoutOpen } from '../settings-cashout';
import { mountCardBrands, termsConsentRow } from '../ui/legal';
// The farm's purchase dialog, reused rather than reimplemented: one confirmation
// wording and one affordability quote for every place currency is spent.
import { confirmPurchase, confirmSellBack } from './farm/hud';
// Every fiat figure on this screen goes through here. A bare number is exactly
// what the shop used to print — "€" was hardcoded in six places, so a player who
// had chosen dollars was quoted euros and then charged dollars.
import {
  BASE_DISPLAY, formatBaseAs, formatMoney, fromBaseMinor, minorToDecimalString,
  parseMinor, toBaseMinor, type DisplayCurrency,
} from '../money';
import { firstSupportedCurrency } from '../../core/payments/methods';

// Fallbacks ONLY: the real ceiling/rate come from GET /shop/wallet → limits, so a
// server-side change can never leave the bar offering amounts the API rejects.
/**
 * Only used when the wallet call failed — the live bound comes from the server
 * (`wallet.fiat.maxCustomDeposit`), which derives it from the €10,000 net-exposure
 * cap. Kept in step with shop.config's MAX_CUSTOM_DEPOSIT_EUR so a fallback bar
 * does not quietly offer a different ceiling from the real one.
 */
const FALLBACK_MAX_CUSTOM_CENTS = 10_000 * 100;
const FALLBACK_SHROOMS_PER_EUR = 100;

/**
 * A shop row. Boosters come from the server catalog (`GET /buffs`) so prices and
 * durations are never duplicated here; the energy/rest shelf comes from
 * `GET /shop/energy-offers`. Only the icon is a client-side choice.
 */
interface Offer {
  id: string;
  name: string;
  desc: string;
  currency: 'coins' | 'shrooms';
  cost: number;
  /**
   * Either a vector icon NAME (boosters, rest charges) or a sprite URL (the
   * keeper's catalogue, which draws the real item). `offerIcon` renders both.
   */
  icon: IconName | string;
  /**
   * Crafting tier to wash the icon with, for armour whose metal the pack does not
   * draw — the tiers above Mythril all wear the plain Iron plate, so without this
   * an Emerald and a Ruby piece are the same picture on the shelf.
   */
  iconTintTier?: number;
  /** Remaining ms if this offer is a timed booster that's currently running. */
  activeMs: number;
  /** One-off purchases (companions) that are already owned — shown, not sold twice. */
  owned?: boolean;
  /** An owned one that can go back for Shrooms: the end price only (no fee shown), and the call. */
  sellBack?: { net: number; loses: string; run: () => Promise<unknown> };
  buy: () => Promise<unknown>;
}

/** Booster kind → icon. Anything unknown falls back to a star. */
const BUFF_ICON: Record<string, IconName> = {
  gather: 'axe', xp: 'star', vigor: 'energy', growth: 'seed', luck: 'gem', ward: 'shield',
};
const OFFER_ICON: Record<string, IconName> = { rest_charge: 'heart', energy_refill: 'energy' };

/**
 * Catalogue row → icon, keyed on the item it GRANTS rather than the row id, so a
 * second gem bundle or a new bar tier gets the right art without a code change.
 * Only existing pack icons are used — no new sprites for this shelf.
 */
/**
 * The keeper's catalogue draws the ITEM, not a category glyph.
 *
 * OWNER: "again the problem we use the same icon Keeper's catalogue … and for bars
 * you are using incorrect one image."
 *
 * Both true, and it was one line: every `bar_*` id mapped to the single `ore`
 * glyph. So the Copper, Iron, Gold and Mythril stacks were four rows with the same
 * picture — and that picture was a lump of ORE, which is the thing you smelt, not
 * the bar you get. The catalogue looked like four ways to buy the same thing.
 *
 * `itemIconSrc` is the bag's resolver over the baked 32x32 icon set, which already
 * holds a distinct sprite for every ore and every bar (build-ore-nodes.py cuts them
 * per metal from the pack's Ores sheet). Using it means the keeper's shelf, the
 * bag and the crafting panel cannot disagree about what a mythril bar looks like,
 * and a new metal needs no change here at all.
 */
const itemIcon = (itemId: string): string => itemIconSrc(itemId);

/**
 * An offer's icon, whichever kind it is.
 *
 * A path is a baked item sprite; anything else is a name in the vector icon set.
 * Two kinds because the shelf sells two kinds of thing — a Rest Charge has no item
 * to draw, a Mythril Bar Stack has nothing but.
 */
function offerIcon(icon: IconName | string, size: number, tintTier?: number): HTMLElement {
  if (!icon.startsWith('/')) return gicon(icon as IconName, size);
  const img = el('img', 'object-contain shrink-0 absolute inset-0 w-full h-full', {
    src: icon, alt: '', style: 'image-rendering:pixelated',
  });
  const colour = tintTier != null ? tierTint(tintTier) : null;
  if (!colour) {
    img.className = 'object-contain shrink-0';
    img.setAttribute('style', `width:${size}px;height:${size}px;image-rendering:pixelated`);
    return img;
  }
  // Same masked wash the bag and the anvil use: the sprite's own alpha is the
  // mask, so only the drawn pixels take the metal and the pack's shading survives.
  const wash = el('span', 'absolute inset-0 pointer-events-none opacity-85', {
    style: `background-color:${colour};-webkit-mask:url(${icon}) center/contain no-repeat;mask:url(${icon}) center/contain no-repeat`,
  });
  return el('span', 'relative inline-block shrink-0', { style: `width:${size}px;height:${size}px;line-height:0` }, img, wash);
}

/** Armour tier -> name. Mirrors ARMOR_TIER_NAME in views/farm/hud.ts (offset one
 *  rung below the tool ladder: armour tier N is made of crafting tier N+1's metal). */
const ARMOUR_TIER_NAME = ['None', 'Stone', 'Copper', 'Iron', 'Gold', 'Mythril', 'Emerald', 'Amber', 'Ruby'];

const fmtMins = (ms: number) => ms > 0 ? `${Math.ceil(ms / 60000)}m left` : '';

// ─── Pixel chrome ────────────────────────────────────────────────────────────
// The shop used to be the one screen built out of generic web cards: rounded
// grey boxes, a system range slider and thin sans headings inside a wooden game.
// These four helpers are the same plank/gold language as the admin panel and the
// arena (2px #5a4632 frame, #322820 header, Terraboma via `.game-font`), so the
// shop reads as part of the world rather than a checkout page bolted onto it.
const PANEL = 'game-font rounded-xl border-2 border-[#5a4632] bg-surface/95 shadow-lift overflow-hidden';
const PLANK = 'game-font rounded-lg border-2 border-[#5a4632] bg-[#322820]';

function panel(title: string, ...children: (Node | string | null)[]): HTMLElement {
  return el('section', PANEL, {},
    el('header', 'px-4 py-2.5 border-b-2 border-[#5a4632] bg-[#322820] text-[#ffd97a] text-sm', {}, title),
    el('div', 'p-4', {}, ...children.filter((c): c is Node | string => c != null)));
}

/** Rarity is the only thing that actually differs between tiers (see below), so
 *  it carries the colour rather than a made-up "best value" badge. */
const RARITY_COLOR: Record<string, string> = {
  Common: '#cbb79b', Uncommon: '#7bc86b', Rare: '#56c6e8', Epic: '#b56bff', Legendary: '#f4c04e',
};

/** Price as a stamped plate — the number the player actually pays, in the
 *  currency he will actually be charged in. */
function priceTag(text: string): HTMLElement {
  return el('div', 'game-font rounded-md border-2 border-[#7a5c3a] bg-[#1b120c] px-2.5 py-1 text-[#ffd97a] text-sm leading-none',
    {}, text);
}

/**
 * A thing that can be bought: a fixed package or a custom amount.
 *
 * The price is carried as INTEGER MINOR UNITS twice over — `chargedMinor` in the
 * player's display currency (what the plate and the Pay button say) and
 * `baseMinor` in EUR (what the server accounts in). Nothing here re-derives one
 * from the other at paint time, so the quote cannot drift from the charge.
 */
interface Buyable {
  name: string;
  shrooms: number;
  /** Set for a fixed package: the server prices it from config by id. */
  packageId?: string;
  /** The currency `chargedMinor` is quoted in, frozen when the tile was opened. */
  currency: string;
  chargedMinor: number;
  /** The EUR accounting amount. Carried so the checkout can be honest about what
   *  will actually be debited when settlement is not in the display currency. */
  baseMinor: number;
  art?: string;
}

type TabId = 'packages' | 'boosters' | 'custom' | 'cashout';

export function renderShop(root: HTMLElement, ctx: GameCtx): ViewTeardown {
  clear(root);
  let packages: Package[] = [];
  let methods: PaymentMethod[] = [];
  /**
   * Which currency `methods` was fetched for.
   *
   * Payment-method availability is a function of (method, currency) — SEPA
   * settles euro and nothing else — so the cached list is only valid for the
   * currency it was asked for, and changing currency has to re-ask the server.
   * Filtering it here instead would put the rule in two places.
   */
  let methodsCurrency = '';
  let wallet: Wallet | null = null;
  let buffs: BuffStatus | null = null;
  let healOffer: HealOffer | null = null;
  /** Shroom price of one extra arena entry, from the arena lobby status. */
  let arenaEntryShrooms: number | null = null;
  /** Set once the operator has opened withdrawals; the "no cash-out" line would then be false. */
  let withdrawalsOpen = false;
  let companions: CompanionStatus | null = null;
  let energyOffers: EnergyOffer[] = [];
  /** The keeper's Shroom catalogue. Empty until it loads, and empty forever if the
   *  endpoint is down — an empty shelf paints its own "nothing here" row. */
  let shroomShelf: ShroomShelf = { hasHut: false, items: [], armour: [] };
  let availableCurrencies: DisplayCurrency[] = [];
  let busy = false;
  let tab: TabId = 'packages';
  let cashoutLoaded = false;

  const summary = el('div', 'mb-3');
  const errorHost = el('div', 'mb-4');
  const tabBar = el('div', 'flex items-end gap-1 border-b-2 border-[#5a4632] mb-4 overflow-x-auto');
  // One body per sub-tab. They are built once and toggled, so switching tabs never
  // re-fetches and never loses the custom amount the player has dialled in.
  const packagesBody = el('div', 'flex flex-col gap-4');
  const boostersBody = el('div', 'flex flex-col gap-4');
  const customBody = el('div', 'flex flex-col gap-4');
  /**
   * Cash out. Empty and tabless until the operator has opened withdrawals — the
   * shop is where money goes IN, and offering a way out that always refuses would
   * be a dead end on the busiest screen in the game. Rendered by the same view the
   * settings panel uses, so there is one cash-out screen, not two.
   */
  const cashoutBody = el('div', 'flex flex-col gap-4 hidden');

  // Packages: 4 per row (2 on narrow screens).
  const packsWrap = el('div', 'grid grid-cols-2 sm:grid-cols-4 gap-3');
  const customWrap = el('div');
  const boostersWrap = el('div', 'grid grid-cols-1 sm:grid-cols-3 gap-3');
  const premiumWrap = el('div', 'grid grid-cols-1 sm:grid-cols-3 gap-3');
  const restWrap = el('div', 'grid grid-cols-1 sm:grid-cols-3 gap-3');
  const itemsWrap = el('div', 'grid grid-cols-1 sm:grid-cols-3 gap-3');
  /** Filled only when the keeper is unreachable (no hut): the rows still render
   *  as a price list, but a dead Buy button with no reason reads as a bug. */
  const itemsNote = el('div', 'game-font text-[11px] text-[#ffb3a7] mb-2 hidden');

  // The rate is flat at every tier, so a "+X% bonus on the big pack" badge would be
  // a lie. Say the true thing — and take the number from the served config (filled
  // in paint()) rather than restating SHROOMS_PER_EUR here.
  const rateNote = el('div', 'game-font text-[11px] text-text-dim px-1 inline-flex items-center gap-1 flex-wrap');
  packagesBody.append(panel('Shroom pouches', packsWrap), rateNote);
  boostersBody.append(
    panel('Rest & Energy', restWrap),
    panel('Premium boosters', premiumWrap),
    // Inside the Boosters tab, not a fourth tab: the tab bar is already three
    // wide and scrolls on mobile. Below the premium boosters because these are
    // Shroom-priced goods rather than a timed effect — the two shroom shelves sit
    // together, and the coin shelf stays last.
    panel("Keeper's catalogue", itemsNote, itemsWrap),
    panel('Coin boosters', boostersWrap));
  customBody.append(customWrap);

  const bodies: Record<TabId, HTMLElement> = {
    packages: packagesBody, boosters: boostersBody, custom: customBody, cashout: cashoutBody,
  };
  const TABS: { id: TabId; label: string }[] = [
    { id: 'packages', label: 'Packages' },
    { id: 'boosters', label: 'Boosters & Energy' },
    { id: 'custom', label: 'Custom amount' },
  ];

  function paintTabs() {
    clear(tabBar);
    for (const t of TABS) {
      const on = t.id === tab;
      tabBar.append(el('button', `tb-tab ${on ? 'tb-tab-on' : 'tb-tab-off'} px-3.5 py-2 text-sm whitespace-nowrap`,
        { onClick: () => { if (tab === t.id) return; tab = t.id; playSfx('ui_open'); paintTabs(); } }, t.label));
    }
    for (const [id, node] of Object.entries(bodies)) node.classList.toggle('hidden', id !== tab);
    // Rendered on first view rather than on load: it fetches the wallet and the
    // request history, and a player who never opens the tab should not pay for it.
    if (tab === 'cashout' && !cashoutLoaded) { cashoutLoaded = true; renderCashout(cashoutBody); }
  }

  root.append(errorHost, summary, tabBar, packagesBody, boostersBody, customBody, cashoutBody);
  paintTabs();

  async function load() {
    [packages, wallet, buffs, companions, energyOffers, shroomShelf] = await Promise.all([
      packages.length ? Promise.resolve(packages) : shopApi.packages(),
      shopApi.wallet(),
      buffsApi.get().catch(() => null),
      companionsApi.get().catch(() => null),
      // NOT cached across loads: a rest-charge offer's price/availability changes the
      // moment you buy one, and the memoised copy kept selling the stale shelf.
      shopApi.energyOffers().catch(() => [] as EnergyOffer[]),
      // Fails soft for the same reason the heal/arena rows do: one shelf missing
      // beats a Shop tab that refuses to open because the forager is down. Never
      // memoised — `hasHut` changes the moment the player builds the hut.
      foragerApi.shroomItems().catch(() => ({ hasHut: false, items: [], armour: [] } as ShroomShelf)),
    ]);
    // Methods are fetched AFTER the wallet, because the wallet is what tells us
    // the currency they have to be valid for. Fails soft to card-only: a shop
    // that will not open because the method list is down is worse than a shop
    // that offers the one method that is actually connected.
    if (methodsCurrency !== cur().code) {
      const wanted = cur().code;
      try {
        const pm = await getPaymentMethods();
        methods = pm.deposit
          .filter((m: DepositMethod) => m.enabled)
          .map((m: DepositMethod) => ({
            id: m.id,
            label: m.label,
            description: m.currencies.join(', ').toUpperCase(),
            active: m.currencies.includes(wanted),
            currencies: m.currencies,
          }));
        withdrawalsOpen = pm.withdrawals.enabled || await cashoutOpen();
        methodsCurrency = wanted;
      } catch { /* keep stale list; retry on next reload */ }
    }
    // Both fail soft: a shelf missing one row is far better than a shop that
    // refuses to open because one side endpoint is down.
    healOffer = await shopApi.healOffer().catch(() => null);
    arenaEntryShrooms = await arenaBrApi.status().then((st) => st.entryCostShrooms ?? null).catch(() => null);
    availableCurrencies = await shopApi.currencies().catch(() => []);
    clear(errorHost);
    paint();

    if (withdrawalsOpen && !TABS.some((t) => t.id === 'cashout')) {
      TABS.push({ id: 'cashout', label: 'Cash out' });
      paintTabs();
    }
  }

  /**
   * A failed first load used to leave the Shop permanently blank — no message, no
   * Retry, and every other section of the app has one. Errors render here instead.
   */
  function showLoadError(e: unknown) {
    clear(errorHost);
    errorHost.append(el('div', 'game-font rounded-lg border-2 border-[#8a3b2e] bg-[#2a1512] p-4 flex flex-wrap items-center gap-3', {},
      el('span', 'flex-1 min-w-0 text-sm text-[#ffb3a7]', {},
        e instanceof ApiError ? e.message : "Couldn't load the shop."),
      el('button', 'tb-pixel-btn px-3 py-1.5 text-sm', { onClick: () => void reload() }, 'Retry')));
  }

  async function reload() {
    clear(errorHost);
    errorHost.append(el('div', 'py-4 text-center text-sm text-text-dim game-font', {}, 'Loading…'));
    try { await load(); } catch (e) { showLoadError(e); }
  }

  /** The served display currency, or EUR until the first wallet lands. Never a
   *  client-side rate table — the server owns the rates. */
  const cur = (): DisplayCurrency => wallet?.currency ?? BASE_DISPLAY;

  function paint() {
    if (!wallet) return;
    clear(summary);
    // The wallet plank: what you hold, and what you have put in so far.
    const walletPlank = el('div', `${PLANK} px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1`, {},
      stat('🍄 Shrooms', wallet.shrooms, 'text-shroom'),
      stat('Deposited', formatBaseAs(wallet.fiat.lifetimeDeposited.base.amountMinor, cur())));
    summary.append(walletPlank);

    if (availableCurrencies.length > 1) {
      const select = el('select', 'tb-pixel-btn px-2 py-1 text-xs bg-[#322820] border border-[#5a4632] rounded cursor-pointer') as HTMLSelectElement;
      for (const c of availableCurrencies) {
        const opt = el('option', '', { value: c.code }, `${c.symbol} ${c.code}`) as HTMLOptionElement;
        if (c.code === cur().code) opt.selected = true;
        select.append(opt);
      }
      select.addEventListener('change', async () => {
        try {
          const updated = await accountApi.setCurrency(select.value);
          if (wallet) wallet.currency = updated;
          paint();
          void reload();
        } catch { toast('Failed to change currency', 'error'); }
      });
      walletPlank.append(
        el('div', 'flex items-center gap-1.5 ml-auto', {},
          el('span', 'text-[10px] text-text-dim uppercase tracking-wider', {}, 'Currency'),
          select));
    }

    clear(rateNote);
    // Quoted as "N Shrooms for <price>" rather than "per €1": in a non-euro
    // display currency there is no round 1-unit price to quote, and inventing one
    // would misstate the rate.
    rateNote.append(...richText(
      `Every pouch pays the same rate — 🍄 ${wallet.limits.shroomsPerEur} for ${formatBaseAs(wallet.limits.shroomsPerEur, cur())}. `
      + `A bigger pouch is fewer checkouts, never a worse deal.`));

    clear(packsWrap);
    // The biggest pouch sets the scale for the "how much is this" bar, so the
    // tiers can be compared at a glance without inventing a bonus.
    const maxShrooms = packages.reduce((m, p) => Math.max(m, p.shrooms), 1);
    for (const p of packages) {
      const rate = Math.round(p.shrooms / p.priceEur);   // Shrooms per €1, config-derived
      // The one place a euro float becomes cents, rounded once at the API
      // boundary; everything downstream is integers.
      const baseMinor = Math.round(p.priceEur * 100);
      const tone = RARITY_COLOR[p.rarity] ?? '#cbb79b';
      packsWrap.append(el('button',
        `${PANEL} p-3 flex flex-col items-center gap-1.5 text-center hover:border-[#e8c15a] active:translate-y-px transition cursor-pointer`,
        { onClick: () => openCheckout({
          name: p.name, shrooms: p.shrooms, art: p.art,
          packageId: p.id,
          currency: cur().code,
          chargedMinor: fromBaseMinor(baseMinor, cur()),
          baseMinor,
        }) },
        el('img', 'w-20 h-20 object-contain', { src: `/packages/${p.art}`, alt: p.name, loading: 'lazy' as any, style: 'image-rendering:pixelated' }),
        el('div', 'game-font text-sm text-[#ffe3a6] leading-tight', {}, p.name),
        el('div', 'text-[10px] uppercase tracking-widest', { style: `color:${tone}` }, `${p.rarity} · ${p.biome}`),
        el('div', 'text-coin font-mono text-sm inline-flex items-center gap-1 justify-center', {}, ...richText(`🍄 ${p.shrooms.toLocaleString()}`)),
        // Relative size bar — pure comparison, no claim about value.
        el('div', 'w-full h-1.5 rounded-full bg-[#1b120c] border border-[#5a4632] overflow-hidden', {},
          el('div', 'h-full bg-shroom', { style: `width:${Math.max(6, Math.round(p.shrooms / maxShrooms * 100))}%` })),
        priceTag(formatBaseAs(baseMinor, cur())),
        el('div', 'text-[10px] text-text-dim font-mono inline-flex items-center gap-1', {},
          ...richText(`🍄 ${rate} for ${formatBaseAs(100, cur())}`))));
    }
    renderOffers();
    renderCustom();
  }

  // ─── Offers: rest/energy, premium boosters, coin boosters ───────────────────
  /** The energy/rest shelf. Both endpoints already existed; this makes them buyable. */
  function restOffers(): Offer[] {
    return energyOffers.map((o) => ({
      id: o.id, name: o.name, desc: o.blurb, currency: o.currency, cost: o.cost,
      icon: OFFER_ICON[o.id] ?? 'energy', activeMs: 0,
      buy: () => (o.id === 'rest_charge' ? fieldApi.buyEnergyExtension() : fieldApi.energyRefill()),
    }));
  }

  /**
   * The two Shroom purchases that existed but were not on any shelf.
   *
   * Owner: "in shop add different items, which you are willing to buy by shrooms."
   * The Shroom shelf already carried the companions and seven boosters — what it
   * did NOT carry was the two things a player most often wants to buy: a healing
   * draught (built, priced and server-validated, but reachable only from a button
   * on the farm's HP bar) and an extra arena entry (only from inside the arena
   * lobby). Both are real offers a player could not find from the Shop tab, which
   * is the one place they would look. Prices come from the server in both cases,
   * so nothing is duplicated here.
   *
   * Deliberately NOT new inventions: the premium economy already has seven
   * boosters, two energy offers and two companions, and adding more sinks on top
   * without a reason would be inventing an economy rather than finishing one.
   */
  function vitalOffers(): Offer[] {
    const out: Offer[] = [];
    if (healOffer) {
      out.push({
        id: healOffer.id, name: healOffer.name, desc: healOffer.blurb,
        currency: 'shrooms', cost: healOffer.cost, icon: 'heart', activeMs: 0,
        buy: async () => { await shopApi.buyHeal(); },
      });
    }
    if (arenaEntryShrooms !== null) {
      out.push({
        id: 'arena_entry', name: 'Arena Entry',
        desc: 'One extra battle-royale entry today, on top of your free daily one.',
        currency: 'shrooms', cost: arenaEntryShrooms, icon: 'swords', activeMs: 0,
        buy: async () => { await arenaBrApi.buyEntries(1); },
      });
    }
    return out;
  }

  /** Timed boosters of one currency, straight from the server catalog. */
  function buffOffers(currency: 'coins' | 'shrooms'): Offer[] {
    return (buffs?.catalog ?? [])
      .filter((b) => b.currency === currency)
      .map((b) => ({
        id: b.kind, name: b.name, desc: `${b.blurb} (${Math.round(b.durationMs / 60000)} min)`,
        currency: b.currency, cost: b.cost, icon: BUFF_ICON[b.kind] ?? 'star',
        activeMs: buffs?.active?.[b.kind] ?? 0,
        buy: () => buffsApi.buy(b.kind),
      }));
  }

  /**
   * The companions — the top of the Shroom shelf.
   *
   * OWNER: "you can buy angel and demon, to be with you, which will kill the
   * nearest mobs, for 15000 shrooms." An owned one is shown as owned rather than
   * hidden: the shelf is also where a player checks what he has, and a tile that
   * vanishes on purchase reads as the money disappearing.
   */
  function companionOffers(): Offer[] {
    return (companions?.catalog ?? []).map((cp) => ({
      id: cp.id, name: cp.name, desc: cp.blurb,
      currency: 'shrooms' as const, cost: cp.cost,
      icon: cp.id === 'angel' ? 'star' : 'fire',
      activeMs: 0,
      owned: companions?.owned.includes(cp.id) ?? false,
      // No farm context here — the shop is its own view. The farm re-reads
      // ownership when it mounts (bootstrap renders each tab fresh), so walking
      // back to the farm is what puts the companion in the world.
      buy: async () => { companions = await companionsApi.buy(cp.id); },
      // Owner (2026-09-24): "allow to sell also demon and angel" — back for the
      // Shrooms paid, minus the same fee as the keeper's goods.
      sellBack: (() => {
        const q = companions?.sellBack?.find((s) => s.kind === cp.id);
        if (!q || !(companions?.owned.includes(cp.id))) return undefined;
        return {
          net: q.net,
          loses: `You receive 🍄 ${q.net} back. You can buy ${cp.name} again later at the shop price.`,
          run: async () => { companions = (await companionsApi.sellBack(cp.id)).status; },
        };
      })(),
    }));
  }

  /**
   * The keeper's Shroom catalogue — bars and gems in fixed bundles.
   *
   * Every number is the server's (GET /forager/shroom-items ← shroom-items.config).
   * Nothing is restated here, and that is not tidiness: the shelf exists under one
   * rule, `shrooms * 10 > quantity * coinValue` — a bundle worth more in Coins than
   * the Shrooms it costs is a money printer, since Shrooms exchange to Coins one
   * way. That rule is checked against the config in shroom-items.test.ts, so a
   * price copied into this file would be a second source of truth the check
   * cannot see.
   *
   * The QUANTITY leads the description on purpose: the row is called "Gem Pouch",
   * which says nothing about whether six gems or one arrive, and `desc` is also
   * what confirmPurchase prints as "Grants" before the Shrooms move.
   */
  function itemOffers(): Offer[] {
    return shroomShelf.items.map((it) => ({
      id: it.id, name: it.name,
      desc: `${it.quantity}× ${it.itemName} — ${it.blurb} (→ your ${it.storage === 'farm' ? 'farm store' : 'bag'})`,
      currency: 'shrooms' as const,
      cost: it.shrooms,
      icon: itemIcon(it.itemId),
      activeMs: 0,
      // dispatchAction, not a bare call: shop.ts's `busy` flag already stops a
      // double tap in THIS tab, but the route is idempotency-gated server-side
      // and only an actionId makes an axios refresh-retry a replay instead of a
      // second 144-Shroom charge.
      buy: () => dispatchAction('forager.shroomItem', (actionId) => foragerApi.buyShroomItem(it.id, actionId),
        { key: it.id }),
    }));
  }

  /**
   * Finished armour — the keeper's smith did the mining for you.
   *
   * One row per slot, never a catalogue of eight: a purchase raises a slot by ONE
   * tier (the server refuses anything else), so listing the rungs above it would
   * be offering things it would reject. A slot at the top of the ladder says so
   * instead of disappearing — "nothing left to buy" is information.
   *
   * The icon is the piece the hero will actually WEAR at that tier, from the same
   * `armourIconSrc` the bag and the anvil use, painted for the tiers whose metal
   * the pack does not draw. A shelf that showed a generic breastplate for all
   * eight rungs would be the keeper-catalogue icon bug again, one shelf over.
   */
  function armourOffers(): Offer[] {
    return (shroomShelf.armour ?? []).flatMap((row) => {
      if (!row.offer) return [];
      const o = row.offer;
      const src = armourIconSrc(row.slot, o.tier);
      return [{
        id: o.id,
        name: o.name,
        desc: row.currentTier > 0
          ? `Upgrades your ${row.slot} from ${ARMOUR_TIER_NAME[row.currentTier] ?? row.currentTier} — forged, not mined (→ worn straight away)`
          : `Your first ${row.slot} — forged, not mined (→ worn straight away)`,
        currency: 'shrooms' as const,
        cost: o.shrooms,
        icon: src ?? 'shield',
        iconTintTier: src ? armourTintTier(o.tier) : undefined,
        activeMs: 0,
        buy: () => dispatchAction('forager.armour', (actionId) => foragerApi.buyArmour(o.id, actionId),
          { key: o.id }),
      }];
    });
  }

  function renderOffers() {
    paintOffers(restWrap, restOffers());
    // Companions first: they are the most expensive thing in the game and the
    // reason this shelf exists past the boosters.
    paintOffers(premiumWrap, [...companionOffers(), ...vitalOffers(), ...buffOffers('shrooms')]);
    clear(itemsNote);
    // Say it once, above the shelf, rather than per row: the buy is hut-gated and
    // would otherwise fail with a toast only after the confirm dialog. Hidden
    // rather than left empty — the note carries a margin.
    const noHut = (shroomShelf.items.length > 0 || (shroomShelf.armour ?? []).length > 0) && !shroomShelf.hasHut;
    itemsNote.classList.toggle('hidden', !noHut);
    if (noHut) itemsNote.append('Build a Forager Hut on your farm to trade with the keeper.');
    // Armour first on this shelf: it is what the player came for once he has a
    // hut, and the bar stacks under it are the do-it-yourself version of the
    // same thing.
    paintOffers(itemsWrap, [...armourOffers(), ...itemOffers()]);
    paintOffers(boostersWrap, buffOffers('coins'));
  }

  function paintOffers(wrap: HTMLElement, offers: Offer[]) {
    clear(wrap);
    if (!offers.length) {
      wrap.append(el('div', 'game-font text-sm text-text-dim py-2', {}, 'Nothing on this shelf right now.'));
      return;
    }
    for (const b of offers) {
      const pay = b.currency === 'shrooms' ? 'shroom' : 'coin';
      const btn = el('button',
        'tb-pixel-btn px-3 py-1.5 text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40 ' +
          (b.currency === 'shrooms' ? '!text-[#dcb6ff]' : '!text-[#ffe3a6]'),
        {}, gicon(pay, 14), String(b.cost)) as HTMLButtonElement;
      btn.addEventListener('click', async () => {
        // `busy` is meaningful again now that confirmPurchase resolves when the
        // dialog is ANSWERED rather than when it is shown — before, two fast taps
        // opened two stacked dialogs and confirming both bought the booster twice.
        if (busy) return;
        busy = true;
        btn.disabled = true;
        try {
        // ASK FIRST. These buttons used to spend on a single click: one tap on a
        // shelf of six near-identical tiles and the currency was gone, with no
        // statement of the price, the balance, or what was left afterwards — and
        // no way back, since a booster starts running the moment it is bought.
        // The farm already had this dialog for rest charges; the shop, which is
        // where most buying actually happens, did not.
        await confirmPurchase({
          title: `Buy ${b.name}?`,
          item: b.name,
          grants: b.desc,
          cost: b.cost,
          currency: b.currency,
          onConfirm: async () => {
            await b.buy();
            playSfx('coin_purchase');
            toast(`${b.name} applied`);
            buffs = await buffsApi.get().catch(() => buffs);
            wallet = await shopApi.wallet().catch(() => wallet);
            // The energy shelf is stateful (a bought rest charge changes what is
            // on offer), so re-read it rather than repainting the pre-purchase copy.
            energyOffers = await shopApi.energyOffers().catch(() => energyOffers);
            // The keeper's catalogue is deliberately NOT re-read: it is a static
            // price table with no stock, and the only per-player field on it
            // (hasHut) cannot change by buying from it.
            await ctx.refreshHud();
            paint();
          },
        });
        } finally { busy = false; btn.disabled = false; }
      });
      const header = el('div', 'flex items-center gap-2', {}, offerIcon(b.icon, 22, b.iconTintTier), el('div', 'game-font text-sm text-[#ffe3a6]', {}, b.name));
      if (b.activeMs > 0) header.append(el('span', 'ml-auto text-[10px] px-1.5 py-0.5 rounded border border-shroom/50 text-shroom font-mono', {}, fmtMins(b.activeMs)));
      // PURPLE FRAME on anything priced in Shrooms (owner: "shroom purchase will be
      // with purple frame, for user to understand"). The two shelves sit in the
      // same panel and the price chip alone is a 14px icon — at a glance the
      // player could not tell which tile would spend real money. The frame is the
      // shroom token itself, so it reads as the same currency the chip does.
      const shroom = b.currency === 'shrooms';
      const owned = b.owned === true;
      if (owned) {
        // A bought companion: no buy button, and say so where the price was.
        btn.disabled = true;
        btn.textContent = '';
        btn.append(el('span', 'text-[11px] text-shroom', {}, 'Owned'));
      }
      const sell = owned && b.sellBack ? b.sellBack : null;
      const sellBtn = sell
        ? el('button',
          'tb-pixel-btn px-3 py-1.5 min-h-[44px] text-[11px] !text-shroom inline-flex items-center justify-center gap-1',
          {}, ...richText(`Sell for 🍄 ${sell.net}`)) as HTMLButtonElement
        : null;
      if (sell && sellBtn) {
        sellBtn.addEventListener('click', async () => {
          if (busy) return;
          busy = true;
          sellBtn.disabled = true;
          try {
            await confirmSellBack({
              item: b.name, net: sell.net, loses: sell.loses,
              onConfirm: async () => {
                await sell.run();
                playSfx('coin_purchase');
                toast(`Sold ${b.name} for 🍄 ${sell.net}`);
                wallet = await shopApi.wallet().catch(() => wallet);
                await ctx.refreshHud();
                paint();
              },
            });
          } finally { busy = false; sellBtn.disabled = false; }
        });
      }
      wrap.append(el('div',
        `${PLANK} p-3 flex flex-col gap-2 ${shroom ? 'border-2 border-shroom/70 shadow-[0_0_10px_rgba(181,107,255,.22)]' : ''}`,
        {},
        header,
        el('div', 'text-[11px] text-text-dim leading-snug flex-1', {}, b.desc),
        btn,
        ...(sellBtn ? [sellBtn] : [])));
    }
  }

  function stat(label: string, value: string | number, cls = 'text-[#ffd97a]') {
    return el('span', 'inline-flex items-center gap-1.5 game-font text-sm', {},
      el('span', 'text-text-dim inline-flex items-center gap-1', {}, ...richText(label)),
      el('b', `font-mono ${cls}`, {}, String(typeof value === 'number' ? value.toLocaleString() : value)));
  }

  // ─── Custom amount bar ───────────────────────────────────────────────────────
  function renderCustom() {
    clear(customWrap);
    // Everything here is integer MINOR UNITS of the player's DISPLAY currency —
    // cents for euros, yen for yen — because that is exactly what
    // POST /shop/deposit/custom takes, and what the server converts inward to
    // EUR itself. The owner wanted a draggable bar and odd amounts like €7.96;
    // both fall out of working in integers. Euro floats put 8.949999999999999
    // on the wire, so nothing here multiplies or divides by 100.
    const c = cur();
    const rate = wallet?.limits.shroomsPerEur ?? FALLBACK_SHROOMS_PER_EUR;
    const MIN = fromBaseMinor(wallet?.limits.minCustomDepositCents ?? 500, c);
    const MAX = fromBaseMinor(wallet?.fiat.maxCustomDeposit.base.amountMinor ?? FALLBACK_MAX_CUSTOM_CENTS, c);
    let minor = fromBaseMinor(1000, c);          // open on a sane €10 rather than 0

    const plain = (v: number) => minorToDecimalString(v, c.minorUnits);
    /** Shrooms bought by an amount in the display currency — via the EUR value,
     *  since 1 Shroom is 1 EUR cent and nothing else. */
    const shroomsFor = (v: number) => toBaseMinor(v, c);

    const slider = el('input',
      'w-full accent-shroom cursor-pointer',
      { type: 'range', min: String(MIN), max: String(MAX), step: '1', value: String(minor) }) as HTMLInputElement;
    const input = el('input',
      'w-28 bg-[#1b120c] border-2 border-[#5a4632] rounded-md px-3 py-2 font-mono text-lg text-center text-[#ffd97a] outline-none focus:border-[#e8c15a]',
      { type: 'text', inputMode: 'decimal', value: plain(minor) }) as HTMLInputElement;
    const preview = el('div', 'text-sm text-text-dim font-mono inline-flex items-center gap-1', {}, ...richText('= 🍄 0'));
    const buyBtn = el('button',
      'tb-pixel-btn px-5 py-2 text-sm !text-[#ffe3a6] disabled:opacity-40 disabled:cursor-not-allowed',
      {}, 'Buy') as HTMLButtonElement;

    /** Repaint everything the amount drives. `typing` leaves the field alone so
     *  the caret does not jump while the player is mid-number. */
    const sync = (typing = false) => {
      clear(preview);
      preview.append(...richText(`= 🍄 ${shroomsFor(minor).toLocaleString()}`));
      slider.value = String(minor);
      if (!typing) input.value = plain(minor);
      buyBtn.disabled = minor < MIN;
    };

    slider.addEventListener('input', () => { minor = Number(slider.value); sync(); });
    input.addEventListener('input', () => {
      // parseMinor accepts both "7.96" and "7,96" — the owner writes prices with
      // a comma, and so does most of Europe — and never multiplies by 100.
      minor = Math.min(MAX, Math.max(0, parseMinor(input.value, c.minorUnits)));
      sync(true);
    });
    // Normalise on blur, so a half-typed "7." settles to "7.00".
    input.addEventListener('blur', () => { minor = Math.max(MIN, minor); sync(); });

    buyBtn.addEventListener('click', () => {
      if (minor < MIN) return;
      const amount = minor;                        // frozen for this checkout
      openCheckout({
        name: 'Custom top-up',
        shrooms: shroomsFor(amount),                 // 1 EUR cent = 1 Shroom
        currency: c.code,
        chargedMinor: amount,
        baseMinor: toBaseMinor(amount, c),
      });
    });
    sync();

    customWrap.append(
      panel('Custom amount',
        el('div', 'flex flex-col gap-3', {},
          el('div', 'flex flex-wrap items-center gap-4', {},
            el('div', 'flex items-center gap-2', {},
              el('span', 'text-text-dim font-mono', {}, c.symbol),
              input),
            preview,
            el('div', 'flex-1 min-w-[8px]'),
            buyBtn),
          slider,
          el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1 flex-wrap', {},
            ...richText(`Drag the bar or type any amount between ${formatMoney(MIN, c)} and ${formatMoney(MAX, c)}`
              + ` · ${formatBaseAs(rate, c)} = 🍄 ${rate}`)))));
  }

  
  /**
   * "Charged as €19.99" — shown only when the display currency is an estimate.
   *
   * `stale` comes from the server's resolved rate: it is true whenever the rate is
   * the invented shipped table or older than the operator's max age, which is
   * exactly when the server will settle in EUR regardless of what is displayed.
   */
  function settlementNote(item: Buyable): HTMLElement | null {
    const c = cur();
    if (c.code === BASE_DISPLAY.code) return null;
    if (!c.stale) return null;
    return el('p', 'text-[11px] font-mono mb-2 text-[#e8c15a]', {},
      `Approximate — you will be charged ${formatMoney(item.baseMinor, BASE_DISPLAY)}.`);
  }

  // ─── Payment popup (modal) ───────────────────────────────────────────────────
  function openCheckout(item: Buyable) {
    const eligible = methods.filter((m) => m.active);
    const shown = eligible.length ? eligible : methods;
    let selected = shown[0]?.id ?? 'card';
    let overlay: HTMLElement | null = null;
    const close = () => { if (overlay) playSfx('ui_close'); overlay?.remove(); overlay = null; document.removeEventListener('keydown', onKey); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };

    const methodList = el('div', 'flex flex-col gap-2 mb-4');
    const renderMethods = () => {
      clear(methodList);
      if (!shown.length) {
        methodList.append(el('div', 'text-sm text-text-dim text-center py-4', {},
          'No payment methods available at the moment.'));
        return;
      }
      for (const m of shown) {
        const on = selected === m.id;
        methodList.append(el('button',
          'flex items-center gap-3 p-3 rounded-md border-2 text-left transition game-font ' +
            (on ? 'border-[#e8c15a] bg-[#322820]' : 'border-[#5a4632] hover:border-[#e8c15a]'),
          { onClick: () => { selected = m.id; renderMethods(); } },
          el('span', 'w-4 h-4 border-2 flex-none ' + (on ? 'border-[#e8c15a]' : 'border-text-dim'), {},
            on ? el('span', 'block w-full h-full scale-[.5] bg-[#e8c15a]') : null),
          el('div', 'flex-1', {},
            el('div', 'text-sm', {}, m.label),
            el('div', 'text-[11px] text-text-dim font-mono', {}, m.description)),
          ));
      }
    };
    renderMethods();

    const showPicker = shown.length > 1;

    // Before any money moves the player can read the Terms (opens over this
    // dialog) and must tick that they accept them: Pay stays disabled until then.
    const payBtn = el('button', 'tb-pixel-btn w-full py-3 text-sm !text-[#ffe3a6] disabled:opacity-50 disabled:cursor-not-allowed',
      { type: 'button', disabled: true, onClick: () => { if (consent.isAccepted()) void pay(item, selected, close); } },
      `Pay ${formatMoney(item.chargedMinor, cur())}`) as HTMLButtonElement;
    const consent = termsConsentRow((ok) => { payBtn.disabled = !ok; });
    // Visa / Mastercard — only the brands the operator has switched on in the
    // admin's payment-method badges; nothing at all when both are off.
    const cardBrands = el('div', 'mb-3 empty:hidden');
    mountCardBrands(cardBrands);

    const card = el('div', `${PANEL} sheet-card w-[420px] max-w-[92vw] max-h-[88vh] overflow-y-auto`, {},
      el('header', 'px-4 py-3 border-b-2 border-[#5a4632] bg-[#322820] flex items-center gap-3', {},
        item.art ? el('img', 'w-12 h-12 object-contain', { src: `/packages/${item.art}`, alt: item.name, style: 'image-rendering:pixelated' }) : el('span'),
        el('div', 'flex-1 min-w-0', {},
          el('div', 'game-font text-[#ffd97a]', {}, item.name),
          el('div', 'text-coin font-mono text-sm inline-flex items-center gap-1', {}, ...richText(`🍄 ${item.shrooms.toLocaleString()}`))),
        priceTag(formatMoney(item.chargedMinor, cur())),
        el('button', 'tb-close-x text-text-dim hover:text-text text-xl leading-none', { onClick: close }, '×')),
      el('div', 'p-4', {},
        showPicker ? el('div', 'text-[10px] uppercase tracking-widest text-text-dim mb-2 font-mono', {}, 'Payment method') : null,
        showPicker ? methodList : null,
        el('div', `${PLANK} px-3 py-2 mb-3 flex items-center justify-between gap-3 text-sm`, {},
          el('span', 'text-text-dim game-font', {}, 'You pay'),
          el('b', 'font-mono text-[#ffd97a]', {}, formatMoney(item.chargedMinor, cur()))),
        // Honest about settlement. The shop DISPLAYS ten currencies, but the
        // card is debited in EUR unless an operator has configured a trusted rate
        // source and switched display-currency charging on. A converted figure is
        // an estimate; saying so is the difference between a price and a surprise.
        settlementNote(item),
        !eligible.length && shown.length
          ? el('p', 'text-[11px] font-mono mb-2 text-[#e8c15a]', {}, `This method settles in EUR — you will be charged ${formatMoney(item.baseMinor, BASE_DISPLAY)}.`)
          : null,
        withdrawalsOpen ? null : el('p', 'text-[11px] text-text-dim font-mono mb-3', {}, 'Card entered on secure page. Shrooms are in-game currency only — no cash-out. No game of chance.'),
        shown.length ? cardBrands : null,
        shown.length ? el('div', 'mb-2', {}, consent.row) : null,
        shown.length
          ? payBtn
          : el('div', 'text-center text-sm text-text-dim py-3', {}, 'Payments are not available right now.')));

    overlay = el('div', 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3', { onClick: (e) => { if (e.target === overlay) close(); } }, card);
    playSfx('ui_open');
    document.body.appendChild(overlay);
    document.addEventListener('keydown', onKey);
  }

  async function pay(item: Buyable, method: string, close: () => void) {
    if (busy) return; busy = true;
    try {
      const m = methods.find((x) => x.id === method);
      const chargeCurrency = (m?.currencies?.length
        ? firstSupportedCurrency({ currencies: m.currencies }, item.currency) ?? 'eur'
        : item.currency).toLowerCase();
      await startDeposit({
        method: method as DepositMethodId,
        ...(item.packageId
          ? { productId: item.packageId }
          : { amountCents: item.baseMinor }),
        currency: chargeCurrency,
        returnPath: '/shop',
      });
      close();
    } catch (e) { toast(e instanceof Error ? e.message : 'Payment failed', 'error'); }
    finally { busy = false; }
  }

  const returnedOrderId = readReturnedOrderId();
  if (returnedOrderId) {
    window.history.replaceState({}, '', window.location.pathname);
    toast('Verifying payment…');
    void pollDepositStatus(returnedOrderId)
      .then((order) => {
        if (order.credited) toast('Payment received — Shrooms credited!');
        else if (order.status === 'PENDING') toast('Payment is still processing — Shrooms will arrive shortly');
        else toast(`Payment ${order.status.toLowerCase()}`, 'error');
        void reload();
      })
      .catch(() => toast('Could not verify payment status', 'error'))
      .finally(() => clearPendingOrder());
  } else {
    const paymentStatus = new URLSearchParams(window.location.search).get('payment');
    if (paymentStatus === 'success') {
      toast('Payment received — Shrooms will arrive shortly');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      toast('Payment cancelled', 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  void reload();
  return () => {};
}
